import React from "react";
import { Outlet, useLocation } from "react-router-dom";
import { Header } from "@/components/Header";
import { CityFooter } from "@/components/CityFooter";
import { CityWordmark } from "@/components/CityBrand";
import { AsLogo } from "@/components/AsLogo";
import { PhoneGate } from "@/components/PhoneGate";
import { SignInPrompt } from "@/components/SignInPrompt";
import { OfflineBanner } from "@/components/OfflineBanner";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { useApp } from "@/state/AppContext";
import { localized } from "@/theme/tenantTheme";

export default function PortalShell() {
  const { t, lang, tenant, mode, businessContext, storefrontBranding } = useApp();
  const location = useLocation();
  const cityName = localized(lang, tenant, "name");
  // Same precedence as Header — see PR5's wiring decision.
  const wl = businessContext?.logo_url ? businessContext : storefrontBranding;
  // Same fix as Header.jsx (L2-002): key white-label chrome off business eligibility, not URL
  // shape, so the plain `/<city>/<slug>` route white-labels too.
  const minimal = mode === "business_site" || location.pathname.startsWith("/b/") || Boolean(wl);
  // The owner console is a full-bleed mobile-first PWA with its own top bar and bottom tab bar
  // (design_handoff_owner_console). It replaces the city chrome (citizen nav, footer) entirely,
  // but every staff console — owner, operator ("/city-desk"), platform admin ("/platform") —
  // must still carry the same platform top bar as the citizen site (AsLogo + lockup on the
  // left, account/language on the right), never the business's own white-label chrome. `Header`
  // renders that bar itself in `consoleMode`, which is a fixed 56px row every console's own
  // sticky sub-bars (owner mobile top bar, desk sidebar) are offset to sit under.
  const ownerConsole = location.pathname === "/owner" || location.pathname.startsWith("/owner/");
  const consoleRoute =
    ownerConsole ||
    location.pathname === "/city-desk" ||
    location.pathname.startsWith("/city-desk/") ||
    location.pathname === "/platform" ||
    location.pathname.startsWith("/platform/");

  if (ownerConsole) {
    return (
      <div className="flex min-h-screen flex-col bg-background">
        <Header consoleMode />
        <OfflineBanner />
        <ErrorBoundary key={location.pathname}>
          <Outlet />
        </ErrorBoundary>
      </div>
    );
  }

  if (consoleRoute) {
    return (
      <div className="flex min-h-screen flex-col bg-background">
        <Header consoleMode />
        <OfflineBanner />
        <main className="flex-1">
          <ErrorBoundary key={location.pathname}>
            <Outlet />
          </ErrorBoundary>
        </main>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <SignInPrompt />
      <PhoneGate />
      <OfflineBanner />
      <Header />
      <main className="flex-1">
        <ErrorBoundary key={location.pathname}>
          <Outlet />
        </ErrorBoundary>
      </main>
      {minimal ? (
        <footer className="border-t border-border bg-muted/30 px-5 py-10 sm:px-8" data-testid="minimal-footer">
          <div className="mx-auto max-w-7xl">
            {wl ? (
              <div data-testid="whitelabel-footer">
                <p className="font-heading text-sm font-semibold">{wl.name}</p>
                <p className="text-xs text-muted-foreground">
                  {t("storefront.whitelabel.footerCopyright", { year: new Date().getFullYear(), name: wl.name })}
                </p>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <AsLogo size={28} className="text-[var(--as-primary)]" />
                <CityWordmark name={cityName} nameHi={tenant?.name_hi} className="text-base" />
              </div>
            )}
          </div>
        </footer>
      ) : (
        <CityFooter />
      )}
    </div>
  );
}
